import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Award, TrendingUp, Play, Download, CheckCircle } from 'lucide-react';
import { getPathway } from '../utils/pathways';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const MOCK_ENROLLED = [
  {
    _id: 'e1', course: { _id: 'c1', title: 'Full-Stack Software Engineer Track', track: 'software-engineering' },
    progress: 45, completedLessons: 21, totalLessons: 48, paymentStatus: 'paid',
    sections: [
      { title: 'Web Fundamentals', lessons: [
        { _id: 'l1', title: 'HTML & CSS Basics', duration: 1800, completed: true },
        { _id: 'l2', title: 'JavaScript Essentials', duration: 2400, completed: true },
        { _id: 'l3', title: 'React Introduction', duration: 3000, completed: false },
      ]},
      { title: 'Backend Development', lessons: [
        { _id: 'l4', title: 'Node.js & Express', duration: 2700, completed: false },
        { _id: 'l5', title: 'MongoDB Basics', duration: 2100, completed: false },
      ]},
    ]
  },
  {
    _id: 'e2', course: { _id: 'c2', title: 'Cybersecurity Fundamentals to Pro', track: 'cybersecurity' },
    progress: 12, completedLessons: 5, totalLessons: 40, paymentStatus: 'paid',
    sections: [
      { title: 'Security Basics', lessons: [
        { _id: 'l6', title: 'Introduction to Cybersecurity', duration: 1500, completed: true },
        { _id: 'l7', title: 'Network Fundamentals', duration: 2000, completed: false },
      ]},
    ]
  },
];

function CourseProgressCard({ enrollment }) {
  const [expanded, setExpanded] = useState(false);
  const pathway = getPathway(enrollment.course.track);
  const tc = pathway.color;

  return (
    <div className="card-edu" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div className="pathway-tile" style={{
          width: 56, height: 56,
          background: tc + '14',
          border: `1px solid ${tc}30`,
        }}>
          <pathway.Icon size={26} color={tc} strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 4 }}>{enrollment.course.title}</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={12} color="var(--ac-green)" /> {enrollment.completedLessons}/{enrollment.totalLessons} lessons
            </span>
            <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{enrollment.progress}% complete</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${enrollment.progress}%`, background: `linear-gradient(90deg, ${tc}, ${tc}aa)` }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`/learn/${enrollment.course._id}`} className="btn btn-primary btn-sm">
            <Play size={14} /> Continue
          </Link>
          <button onClick={() => setExpanded(!expanded)} className="btn btn-ghost btn-sm">
            {expanded ? 'Hide' : 'Lessons'}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--ac-border)', paddingTop: '1rem' }}>
          {enrollment.sections?.map((section, si) => (
            <div key={si} style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ac-text)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{section.title}</h4>
              {section.lessons.map((lesson, li) => (
                <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 4, background: lesson.completed ? 'rgba(0,220,130,0.05)' : 'rgba(255,255,255,0.02)', border: `1px solid ${lesson.completed ? 'rgba(0,220,130,0.15)' : 'var(--ac-border)'}` }}>
                  {lesson.completed
                    ? <CheckCircle size={16} color="var(--ac-green)" />
                    : <Play size={16} color="var(--ac-cyan)" style={{ opacity: 0.6 }} />
                  }
                  <span style={{ flex: 1, fontSize: 13, color: lesson.completed ? 'var(--ac-muted)' : 'var(--ac-text)' }}>{lesson.title}</span>
                  <span style={{ fontSize: 11, color: 'var(--ac-muted)' }}>{Math.floor(lesson.duration / 60)}m</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState(MOCK_ENROLLED);
  const [activeTab, setActiveTab] = useState('courses');

  useEffect(() => {
    api.get('/courses/my-enrollments')
      .then(({ data }) => { if (data?.data?.length) setEnrollments(data.data); })
      .catch(() => {}); // silently fall back to mock data
  }, []);

  const totalProgress = enrollments.length > 0
    ? Math.round(enrollments.reduce((a, e) => a + e.progress, 0) / enrollments.length)
    : 0;

  const stats = [
    { Icon: BookOpen, label: 'Enrolled Courses', value: enrollments.length, color: 'var(--ac-cyan)' },
    { Icon: CheckCircle, label: 'Lessons Completed', value: enrollments.reduce((a, e) => a + e.completedLessons, 0), color: 'var(--ac-green)' },
    { Icon: TrendingUp, label: 'Avg. Progress', value: `${totalProgress}%`, color: 'var(--ac-purple)' },
    { Icon: Award, label: 'Certificates', value: enrollments.filter(e => e.progress === 100).length, color: 'var(--ac-pink)' },
  ];

  const tabs = ['courses', 'downloads', 'certificates'];

  return (
    <div style={{ paddingTop: 68, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: `radial-gradient(ellipse at 20% 50%, rgba(168,85,247,0.07) 0%, transparent 55%), var(--ac-navy)`, borderBottom: '1px solid var(--ac-border)', padding: '2.5rem 0' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', marginBottom: '2rem' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--grad-edu)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: 'white' }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--ac-white)' }}>Welcome back, {user?.name?.split(' ')[0]}!</h1>
              <p style={{ fontSize: 14, color: 'var(--ac-muted)' }}>Keep up the great work on your learning journey.</p>
            </div>
          </div>
          {/* Stats */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {stats.map(({ Icon, label, value, color }, i) => (
              <div key={i} style={{ flex: '1 1 160px', background: 'var(--ac-card)', border: '1px solid var(--ac-border)', borderRadius: 'var(--radius-md)', padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ac-white)' }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--ac-muted)' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--ac-border)', background: 'var(--ac-navy)' }}>
        <div className="container" style={{ display: 'flex', gap: 0 }}>
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '1rem 1.5rem', background: 'none', border: 'none',
              color: activeTab === tab ? 'var(--ac-purple)' : 'var(--ac-muted)',
              fontWeight: activeTab === tab ? 700 : 400,
              fontSize: 14, cursor: 'pointer', textTransform: 'capitalize',
              borderBottom: `2px solid ${activeTab === tab ? 'var(--ac-purple)' : 'transparent'}`,
              transition: 'var(--transition)',
            }}>{tab}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        {activeTab === 'courses' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ac-white)' }}>My Courses</h2>
              <Link to="/educenter" className="btn btn-outline-purple btn-sm">+ Browse More Courses</Link>
            </div>
            {enrollments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--ac-muted)' }}>
                <BookOpen size={48} style={{ marginBottom: '1rem', opacity: 0.4 }} />
                <p style={{ marginBottom: '1.5rem' }}>You haven't enrolled in any courses yet.</p>
                <Link to="/educenter" className="btn btn-edu">Browse Courses</Link>
              </div>
            ) : (
              enrollments.map(e => <CourseProgressCard key={e._id} enrollment={e} />)
            )}
          </div>
        )}

        {activeTab === 'downloads' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '1.5rem' }}>Practice Files</h2>
            <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--ac-muted)' }}>
              <Download size={36} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>Practice files from your enrolled courses will appear here.</p>
            </div>
          </div>
        )}

        {activeTab === 'certificates' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '1.5rem' }}>Certificates</h2>
            <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--ac-muted)' }}>
              <Award size={36} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>Complete a course to earn your AlteraCode certificate.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
